import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Radio, Select, Switch, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import EditableRowCard from "./EditableRowCard";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import AttributeSelect from "./AttributeSelect";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import EnumHelpTable, { type EnumHelpRow } from "./EnumHelpTable";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
import { MonitoringMode } from "../../../../../flatbuffers/generated/opc-ua/monitoring-mode";
import { DataChangeTrigger } from "../../../../../flatbuffers/generated/opc-ua/data-change-trigger";
import { DeadbandType } from "../../../../../flatbuffers/generated/opc-ua/deadband-type";
// INTERFACES
import type {
  OpcUaMonitoringParametersForm,
  OpcUaMonitorModeForm,
  OpcUaNamedSubscriptionForm,
  OpcUaNodeIdForm,
  OpcUaNodeMonitorForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const BLANK_PARAMS: OpcUaMonitoringParametersForm = {
  samplingIntervalMs: -1,
  queueSize: 1,
  discardOldest: true,
  monitoringMode: MonitoringMode.Reporting,
  dataChangeTrigger: DataChangeTrigger.StatusValue,
  deadbandType: DeadbandType.None_,
  deadbandValue: 0,
};

const MONITORING_MODE_OPTIONS: { value: MonitoringMode; label: string }[] = [
  { value: MonitoringMode.Disabled, label: "Disabled" },
  { value: MonitoringMode.Sampling, label: "Sampling" },
  { value: MonitoringMode.Reporting, label: "Reporting" },
];

const DATA_CHANGE_TRIGGER_OPTIONS: { value: DataChangeTrigger; label: string }[] = [
  { value: DataChangeTrigger.Status, label: "Status" },
  { value: DataChangeTrigger.StatusValue, label: "Status + Value" },
  { value: DataChangeTrigger.StatusValueTimestamp, label: "Status + Value + Timestamp" },
];

const DEADBAND_TYPE_OPTIONS: { value: DeadbandType; label: string }[] = [
  { value: DeadbandType.None_, label: "None" },
  { value: DeadbandType.Absolute, label: "Absolute" },
  { value: DeadbandType.Percent, label: "Percent" },
];

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop). */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

function describeMode(mode: OpcUaMonitorModeForm): string {
  if (mode.kind === "poll") return `poll every ${mode.intervalMs}ms`;
  return `subscribe(${mode.subscriptionName})`;
}

/**
 * True when a Subscribe-mode monitor's `subscriptionName` cannot resolve to
 * anything real: either it names a subscription that isn't in
 * `namedSubscriptions`, or it's empty ("use the connection default") while no
 * default subscription is configured. A monitor in this state is silently
 * dead — the server never receives anything to attach it to.
 */
function isUnresolvedSubscription(
  mode: OpcUaMonitorModeForm,
  namedSubscriptions: OpcUaNamedSubscriptionForm[],
  hasDefaultSubscription: boolean,
): boolean {
  if (mode.kind !== "subscribe") return false;
  if (mode.subscriptionName === "") return !hasDefaultSubscription;
  return !namedSubscriptions.some((named) => named.name === mode.subscriptionName);
}

/** Mode-specific fields: Subscribe references a named subscription; Poll sets a read interval. */
function MonitorModeFields({
  mode,
  onChangeMode,
  namedSubscriptions,
  hasDefaultSubscription,
}: {
  mode: OpcUaMonitorModeForm;
  onChangeMode: (mode: OpcUaMonitorModeForm) => void;
  namedSubscriptions: OpcUaNamedSubscriptionForm[];
  hasDefaultSubscription: boolean;
}) {
  const [t] = useTranslation("global");

  const modeHelpRows: EnumHelpRow[] = [
    { value: "Subscribe", meaning: t("general.opcUaMonitorModeSubscribeDesc") },
    { value: "Poll", meaning: t("general.opcUaMonitorModePollDesc") },
  ];

  const unresolved =
    mode.kind === "subscribe" &&
    isUnresolvedSubscription(mode, namedSubscriptions, hasDefaultSubscription);
  const missingDefault = mode.kind === "subscribe" && mode.subscriptionName === "" && unresolved;

  // No wrapping `<div className="row">` here on purpose: the caller renders
  // Attribute, Mode and Subscription Name/Poll Interval together as one
  // 3-per-row group, so this returns just its own columns into that shared row.
  return (
    <>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label="Mode"
          tooltip={
            <EnumHelpTable
              valueHeader={t("general.opcUaEnumTableValue")}
              meaningHeader={t("general.opcUaEnumTableMeaning")}
              rows={modeHelpRows}
            />
          }
        />
        <div style={{ marginTop: 8 }}>
          <Radio.Group
            value={mode.kind}
            onChange={(e) =>
              onChangeMode(
                e.target.value === "poll"
                  ? { kind: "poll", intervalMs: 1000 }
                  : { kind: "subscribe", subscriptionName: "" },
              )
            }
          >
            <Radio value="subscribe">Subscribe</Radio>
            <Radio value="poll">Poll</Radio>
          </Radio.Group>
        </div>
      </div>
      {mode.kind === "subscribe" && (
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel
            label="Subscription Name"
            tooltip={t("general.opcUaSubscriptionNameHelp")}
          />
          <Select
            className="generalStyles__width100"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            value={mode.subscriptionName}
            onChange={(v: string) => onChangeMode({ kind: "subscribe", subscriptionName: v })}
          >
            <Option value="">{t("general.opcUaSubscriptionNameDefaultOption")}</Option>
            {namedSubscriptions.map((named) => (
              <Option key={named.name} value={named.name}>
                {named.name}
              </Option>
            ))}
          </Select>
          {unresolved && (
            <div role="alert" style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {t(
                missingDefault
                  ? "general.opcUaSubscriptionDefaultMissingWarning"
                  : "general.opcUaSubscriptionNameUnresolvedWarning",
              )}
            </div>
          )}
        </div>
      )}
      {mode.kind === "poll" && (
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel
            label="Poll Interval (ms)"
            tooltip={t("general.opcUaMonitorPollIntervalHelp")}
          />
          <InputNumber
            className="generalStyles__width100"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            min={0}
            value={mode.intervalMs}
            onChange={(v) => onChangeMode({ kind: "poll", intervalMs: v ?? 0 })}
          />
        </div>
      )}
    </>
  );
}

/** Optional per-item monitoring parameters. A switch toggles between server defaults (null) and an explicit config. */
function MonitoringParamsFields({
  params,
  onChangeParams,
}: {
  params: OpcUaMonitoringParametersForm | null;
  onChangeParams: (params: OpcUaMonitoringParametersForm | null) => void;
}) {
  const [t] = useTranslation("global");
  const onToggleEnabled = (enabled: boolean) =>
    onChangeParams(enabled ? { ...BLANK_PARAMS } : null);
  const onChangeField = <K extends keyof OpcUaMonitoringParametersForm>(
    field: K,
    value: OpcUaMonitoringParametersForm[K],
  ) => onChangeParams({ ...(params ?? BLANK_PARAMS), [field]: value });

  const monitoringModeHelpRows: EnumHelpRow[] = [
    { value: "Disabled", meaning: t("general.opcUaMonitoringModeDisabledDesc") },
    { value: "Sampling", meaning: t("general.opcUaMonitoringModeSamplingDesc") },
    { value: "Reporting", meaning: t("general.opcUaMonitoringModeReportingDesc") },
  ];
  const dataChangeTriggerHelpRows: EnumHelpRow[] = [
    { value: "Status", meaning: t("general.opcUaDataChangeTriggerStatusDesc") },
    { value: "Status + Value", meaning: t("general.opcUaDataChangeTriggerStatusValueDesc") },
    {
      value: "Status + Value + Timestamp",
      meaning: t("general.opcUaDataChangeTriggerStatusValueTimestampDesc"),
    },
  ];
  const deadbandTypeHelpRows: EnumHelpRow[] = [
    { value: "None", meaning: t("general.opcUaDeadbandTypeNoneDesc") },
    { value: "Absolute", meaning: t("general.opcUaDeadbandTypeAbsoluteDesc") },
    { value: "Percent", meaning: t("general.opcUaDeadbandTypePercentDesc") },
  ];

  return (
    <div className="mt-2">
      <div style={{ marginBottom: 8 }}>
        <OpcUaFieldLabel label="Configure monitoring parameters" />
        <div style={{ marginTop: 8 }}>
          <Switch checked={params !== null} onChange={onToggleEnabled} />
        </div>
      </div>
      {params && (
        <div className="row">
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel
              label="Sampling Interval (ms)"
              tooltip={t("general.opcUaMonitorSamplingIntervalHelp")}
            />
            <InputNumber
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={params.samplingIntervalMs}
              onChange={(v) => onChangeField("samplingIntervalMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel label="Queue Size" tooltip={t("general.opcUaMonitorQueueSizeHelp")} />
            <InputNumber
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              min={0}
              value={params.queueSize}
              onChange={(v) => onChangeField("queueSize", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel
              label="Monitoring Mode"
              tooltip={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={monitoringModeHelpRows}
                />
              }
            />
            <Select
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={params.monitoringMode}
              onChange={(v: MonitoringMode) => onChangeField("monitoringMode", v)}
            >
              {MONITORING_MODE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel
              label="Data Change Trigger"
              tooltip={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={dataChangeTriggerHelpRows}
                />
              }
            />
            <Select
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={params.dataChangeTrigger}
              onChange={(v: DataChangeTrigger) => onChangeField("dataChangeTrigger", v)}
            >
              {DATA_CHANGE_TRIGGER_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel
              label="Deadband Type"
              tooltip={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={deadbandTypeHelpRows}
                />
              }
            />
            <Select
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={params.deadbandType}
              onChange={(v: DeadbandType) => onChangeField("deadbandType", v)}
            >
              {DEADBAND_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <OpcUaFieldLabel label="Deadband Value" tooltip={t("general.opcUaDeadbandValueHelp")} />
            <InputNumber
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={params.deadbandValue}
              onChange={(v) => onChangeField("deadbandValue", v ?? 0)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** The fields of one monitor: node, attribute, mode and optional monitoring parameters. Shared by the create form and a card's in-place edit. */
function MonitorForm({
  monitor,
  onChangeMonitor,
  namedSubscriptions,
  hasDefaultSubscription,
  disabled = false,
}: {
  monitor: OpcUaNodeMonitorForm;
  onChangeMonitor: (monitor: OpcUaNodeMonitorForm) => void;
  namedSubscriptions: OpcUaNamedSubscriptionForm[];
  hasDefaultSubscription: boolean;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");
  return (
    // While the create form is parked behind an in-place edit, the nested
    // editors (which have no disabled prop of their own) are frozen too.
    <div
      aria-disabled={disabled || undefined}
      style={disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <NodeIdEditor
        node={monitor.node}
        onChangeNode={(node) => onChangeMonitor({ ...monitor, node })}
        disabled={disabled}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-4">
          <AttributeSelect
            value={monitor.attributeId}
            onChange={(attributeId) => onChangeMonitor({ ...monitor, attributeId })}
            tooltip={t("general.opcUaMonitorAttributeHelp")}
          />
        </div>
        <MonitorModeFields
          mode={monitor.mode}
          onChangeMode={(mode) => onChangeMonitor({ ...monitor, mode })}
          namedSubscriptions={namedSubscriptions}
          hasDefaultSubscription={hasDefaultSubscription}
        />
      </div>
      <MonitoringParamsFields
        params={monitor.params}
        onChangeParams={(params) => onChangeMonitor({ ...monitor, params })}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-6">
          {monitor.params && (
            <>
              <OpcUaFieldLabel
                label="Discard Oldest"
                tooltip={t("general.opcUaDiscardOldestHelp")}
              />
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={monitor.params.discardOldest}
                  onChange={(checked) =>
                    onChangeMonitor({
                      ...monitor,
                      params: {
                        ...(monitor.params as OpcUaMonitoringParametersForm),
                        discardOldest: checked,
                      },
                    })
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const blankMonitor = (): OpcUaNodeMonitorForm => ({
  node: { ...BLANK_NUMERIC_NODE_ID },
  mode: { kind: "subscribe", subscriptionName: "" },
  attributeId: AttributeId.Value,
  params: null,
});

/** The two-line read of a stored monitor: mode and attribute tags over the raw node reference and mode. */
function summarizeMonitor(monitor: OpcUaNodeMonitorForm) {
  const tags = (
    <>
      <Tag style={{ textAlign: "center" }}>
        {monitor.mode.kind === "poll" ? "Poll" : "Subscribe"}
      </Tag>
      <Tag style={{ textAlign: "center" }}>{AttributeId[monitor.attributeId]}</Tag>
    </>
  );
  return { tags, raw: `${describeNode(monitor.node)} — ${describeMode(monitor.mode)}` };
}

/** Per-node monitor configuration list: which node, observed how (Subscribe/Poll), which attribute, with which parameters. Rows are created, edited in place, and deleted. */
export default function MonitorsEditor({
  monitors,
  onChangeMonitors,
  namedSubscriptions,
  hasDefaultSubscription,
}: {
  monitors: OpcUaNodeMonitorForm[];
  onChangeMonitors: (rows: OpcUaNodeMonitorForm[]) => void;
  namedSubscriptions: OpcUaNamedSubscriptionForm[];
  hasDefaultSubscription: boolean;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaNodeMonitorForm>(blankMonitor);
  // null = authoring a new row with the create form; an index = editing that
  // stored row in place (its card grows the form, the create form is disabled).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const canAdd = isNodeComplete(draft.node);
  const onSubmit = () => {
    if (!canAdd) return;
    if (editingIndex !== null) {
      const updated = [...monitors];
      updated[editingIndex] = draft;
      onChangeMonitors(updated);
      setEditingIndex(null);
    } else {
      onChangeMonitors([...monitors, draft]);
    }
    setDraft(blankMonitor());
  };
  const onEdit = (index: number) => {
    setDraft(monitors[index]);
    setEditingIndex(index);
  };
  const onCancelEdit = () => {
    setDraft(blankMonitor());
    setEditingIndex(null);
  };
  const onRemove = (index: number) => {
    onChangeMonitors(monitors.filter((_, i) => i !== index));
    if (editingIndex === index) onCancelEdit();
  };

  const isEditing = editingIndex !== null;

  return (
    <div className="container" style={{ width: "100%", maxWidth: "none" }}>
      <Collapse defaultActiveKey={["monitors"]}>
        <Panel header={t("general.opcUaNodeMonitors", { total: monitors.length })} key="monitors">
          {monitors.map((stored, index) => {
            const editing = editingIndex === index;
            // While editing, the summary follows the draft so it reacts to every change.
            const monitor = editing ? draft : stored;
            const { tags, raw } = summarizeMonitor(monitor);
            const rowUnresolved = isUnresolvedSubscription(
              monitor.mode,
              namedSubscriptions,
              hasDefaultSubscription,
            );
            const rowMissingDefault =
              rowUnresolved &&
              monitor.mode.kind === "subscribe" &&
              monitor.mode.subscriptionName === "";
            return (
              <EditableRowCard
                key={`${stored.node.namespaceIndex}-${stored.node.identifier}-${index}`}
                borderColor="rgba(255, 255, 255, 0.15)"
                tags={tags}
                raw={
                  <>
                    {raw}
                    {rowUnresolved && (
                      <div role="alert" style={{ color: "#ff4d4f", fontSize: 12 }}>
                        {t(
                          rowMissingDefault
                            ? "general.opcUaSubscriptionDefaultMissingWarning"
                            : "general.opcUaSubscriptionNameUnresolvedWarning",
                        )}
                      </div>
                    )}
                  </>
                }
                editing={editing}
                dimmed={isEditing && !editing}
                canSave={canAdd}
                deleteTitle="Delete this monitor? This cannot be undone."
                onEdit={() => onEdit(index)}
                onSave={onSubmit}
                onCancel={onCancelEdit}
                onRemove={() => onRemove(index)}
              >
                <MonitorForm
                  monitor={draft}
                  onChangeMonitor={setDraft}
                  namedSubscriptions={namedSubscriptions}
                  hasDefaultSubscription={hasDefaultSubscription}
                />
              </EditableRowCard>
            );
          })}
          <MonitorForm
            monitor={draft}
            onChangeMonitor={setDraft}
            namedSubscriptions={namedSubscriptions}
            hasDefaultSubscription={hasDefaultSubscription}
            disabled={isEditing}
          />
          <div className="row mt-2">
            <div className="col-12">
              <Button
                onClick={onSubmit}
                disabled={!canAdd || isEditing}
                className={`generalStyles__width100 ${canAdd && !isEditing ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
